import React, { useState } from 'react';
import './AIIndicators.css';

const INDICATORS = [
  {
    id: 'vwap_analysis',
    icon: '📊',
    name: 'VWAP Analysis',
    category: 'Intraday',
    difficulty: 'Beginner',
    edge: 'High',
    description: 'Volume Weighted Average Price is the single most important intraday indicator. Institutions use it as their benchmark — price above VWAP means buyers in control, below means sellers.',
    signals: [
      { label: 'Long Setup', desc: 'Price bounces off VWAP from above on first test — buy the dip back to VWAP', color: '#22c55e' },
      { label: 'Short Setup', desc: 'Price fails to reclaim VWAP after multiple attempts — sell the rip into VWAP', color: '#ef4444' },
      { label: 'Breakout', desc: 'Price crosses VWAP with 3x+ volume — momentum trade in direction of cross', color: '#60a5fa' },
    ],
    bestFor: ['Day trading', 'Momentum', 'Scalping'],
    timeframes: ['1m', '5m'],
    rules: [
      'Only buy above VWAP, only short below VWAP',
      'First VWAP test of the day has highest probability',
      'VWAP reclaim with volume = strong bullish signal',
      'Avoid trading into VWAP from the wrong side',
    ],
    stats: 'Studies show VWAP strategies produce 58-67% win rates on trending days',
  },
  {
    id: 'rvol_momentum',
    icon: '🔥',
    name: 'Relative Volume (RVOL)',
    category: 'Momentum',
    difficulty: 'Beginner',
    edge: 'Very High',
    description: 'RVOL compares today\'s volume to the average volume for this time of day. RVOL >5x means something significant is happening — news, catalyst, or unusual institutional activity.',
    signals: [
      { label: 'Momentum Signal', desc: 'RVOL >5x with price up >10% — strong momentum play', color: '#22c55e' },
      { label: 'Capitulation', desc: 'RVOL >10x on a down move — possible exhaustion reversal', color: '#f59e0b' },
      { label: 'Volume Dry-Up', desc: 'RVOL <0.5x — avoid trading, no institutional interest', color: '#484f58' },
    ],
    bestFor: ['Gap plays', 'News catalysts', 'Small caps'],
    timeframes: ['1m', '5m', 'Pre-market'],
    rules: [
      'RVOL >5x required before entering any momentum trade',
      'Higher RVOL = more reliable the move',
      'Compare RVOL at the same time each day for fair comparison',
      'RVOL spike without news = possible halt coming',
    ],
    stats: 'Stocks with RVOL >10x on catalyst days produce average moves of 25-40%',
  },
  {
    id: 'bb_squeeze',
    icon: '🗜',
    name: 'Bollinger Band Squeeze',
    category: 'Volatility',
    difficulty: 'Intermediate',
    edge: 'High',
    description: 'When Bollinger Bands contract inside Keltner Channels, the market is coiling like a spring. This compression always resolves in an explosive move — the squeeze tells you WHEN, not which direction.',
    signals: [
      { label: 'Squeeze On', desc: 'Bands inside Keltner Channels — volatility compressed, big move incoming', color: '#f59e0b' },
      { label: 'Bullish Release', desc: 'Squeeze fires upward with momentum histogram turning green — buy breakout', color: '#22c55e' },
      { label: 'Bearish Release', desc: 'Squeeze fires downward with red histogram — short the breakdown', color: '#ef4444' },
    ],
    bestFor: ['Swing trading', 'Pre-earnings', 'Breakout plays'],
    timeframes: ['15m', '1H', 'Daily'],
    rules: [
      'Never trade the squeeze itself — wait for the release',
      'Longer the squeeze, bigger the eventual move',
      'Confirm direction with volume and momentum before entry',
      'TTM Squeeze is the most popular implementation',
    ],
    stats: 'Bollinger Band squeezes precede moves of 15%+ in 70% of cases on daily charts',
  },
  {
    id: 'opening_range',
    icon: '🎯',
    name: 'Opening Range Breakout (ORB)',
    category: 'Intraday',
    difficulty: 'Beginner',
    edge: 'Very High',
    description: 'The first 15 minutes of trading sets the high and low of the opening range. A break above that range with volume = one of the most reliable day trading setups in existence.',
    signals: [
      { label: 'ORB Long', desc: 'Price breaks above the 9:30-9:45am high with 2x+ volume — momentum long', color: '#22c55e' },
      { label: 'ORB Short', desc: 'Price breaks below the 9:30-9:45am low with volume — momentum short', color: '#ef4444' },
      { label: 'False Break', desc: 'Price breaks range but immediately reverses — fade the move back into range', color: '#a78bfa' },
    ],
    bestFor: ['Day trading', 'Gap & Go', 'Momentum'],
    timeframes: ['1m', '2m', '5m'],
    rules: [
      'Wait for the full 15 minutes before the range is set',
      'Higher RVOL on the breakout bar = higher probability',
      'Target: 1-2x the size of the opening range as profit target',
      'Stop: back below the breakout level (or 50% of ORB)',
    ],
    stats: '5-minute ORB breakouts succeed 61% of the time on gap-up days with news',
  },
  {
    id: 'float_rotation',
    icon: '🌀',
    name: 'Float Rotation',
    category: 'Small Cap',
    difficulty: 'Intermediate',
    edge: 'Very High',
    description: 'When volume traded exceeds the total float (shares available), every shareholder has theoretically traded. Multiple float rotations = extreme momentum. This is the #1 indicator for small cap runners.',
    signals: [
      { label: '1x Rotation', desc: 'Volume = float size — strong momentum, could run more', color: '#f59e0b' },
      { label: '3x+ Rotation', desc: 'Volume is 3x the float — extreme squeeze, likely to continue', color: '#22c55e' },
      { label: 'Exhaustion', desc: 'Float rotated 5x+ with price stalling — possible top', color: '#ef4444' },
    ],
    bestFor: ['Small cap momentum', 'Short squeeze', 'Low float plays'],
    timeframes: ['1m', '5m'],
    rules: [
      'Float under 10M = "rocket fuel" — small supply, big moves',
      'Calculate: Volume / Float = Rotations',
      'Multiple rotations early in day = strong continuation signal',
      'High rotation + news = potential multi-day runner',
    ],
    stats: 'Stocks with <5M float and 3x+ rotation average 35% intraday moves',
  },
  {
    id: 'ipo_strategy',
    icon: '🚀',
    name: 'IPO Trading Strategy',
    category: 'Catalyst',
    difficulty: 'Advanced',
    edge: 'Very High',
    description: 'IPO day and the first 30 days of trading offer unique opportunities. No historical chart means pure price action — but the first 15 minutes establish critical levels that often hold for weeks.',
    signals: [
      { label: 'IPO Day Long', desc: 'Price holds above the IPO opening price first hour — momentum long into VWAP extensions', color: '#22c55e' },
      { label: 'IPO Fade', desc: 'Price breaks below opening price on volume — short the hype fade', color: '#ef4444' },
      { label: '30-Day Setup', desc: 'First 30-day high breaks on volume — institutional accumulation signal', color: '#60a5fa' },
    ],
    bestFor: ['Momentum', 'Catalyst plays', 'High volatility'],
    timeframes: ['1m', '5m', 'Daily'],
    rules: [
      'Never hold an IPO overnight on day 1 — spreads are wide',
      'Use the opening price as your key support/resistance',
      'Watch for the lockup expiration (180 days) — often causes selloff',
      'High-profile IPOs with >10x RVOL = best opportunity',
    ],
    stats: 'IPOs with positive first-day momentum carry 3x average volume for 5 trading days',
  },
  {
    id: 'reverse_split',
    icon: '⚠️',
    name: 'Reverse Split Scanner',
    category: 'Special Situation',
    difficulty: 'Advanced',
    edge: 'High',
    description: 'Reverse splits artificially inflate a stock price to meet exchange listing requirements. The stock almost always continues lower — but the day-of volatility creates powerful short and momentum plays.',
    signals: [
      { label: 'Pre-Split Squeeze', desc: 'Stock spikes before reverse split announcement — momentum long into the news', color: '#22c55e' },
      { label: 'Post-Split Fade', desc: 'After reverse split executes, stock fades back — short the pop', color: '#ef4444' },
      { label: 'Dilution Watch', desc: 'Monitor shelf offerings post-split — company will raise capital at the new higher price', color: '#f59e0b' },
    ],
    bestFor: ['Short selling', 'Special situations', 'News plays'],
    timeframes: ['1m', '5m', 'Daily'],
    rules: [
      'Reverse splits are almost always bearish long-term',
      'The split itself can cause a short-term pump — play the momentum',
      'Check outstanding shares before and after split',
      'Track days since split — most fades happen within 30 days',
    ],
    stats: '87% of reverse split stocks trade lower than their post-split price within 6 months',
  },
  {
    id: 'afterhours_breakout',
    icon: '🌙',
    name: 'After Hours Breakout Setup',
    category: 'Extended Hours',
    difficulty: 'Intermediate',
    edge: 'High',
    description: 'After-hours movers become the next morning\'s pre-market watchlist. A stock that breaks out after 4pm with volume and a catalyst is primed to gap up and continue the following morning.',
    signals: [
      { label: 'AH Runner', desc: 'Stock up >20% after hours on earnings/news — add to next day watchlist', color: '#22c55e' },
      { label: 'AH Fade', desc: 'Stock gaps up big AH but fades back — potential Gap & Crap next morning', color: '#ef4444' },
      { label: 'Consolidation', desc: 'AH move consolidates in tight range — ready for pre-market continuation', color: '#60a5fa' },
    ],
    bestFor: ['Pre-market prep', 'Earnings plays', 'Continuation setups'],
    timeframes: ['AH', 'Pre-market', '1m'],
    rules: [
      'Biggest AH movers become next morning\'s top gappers',
      'Check if gap is news-driven — has to have a real catalyst',
      'The pre-market high is the key level to watch at open',
      'Gap & Go works best when the pre-market trend is clean',
    ],
    stats: 'Stocks gapping >15% with news maintain >50% of the gap through the first hour 64% of the time',
  },
  {
    id: 'supertrend_ai',
    icon: '🤖',
    name: 'SuperTrend AI',
    category: 'AI Indicator',
    difficulty: 'Beginner',
    edge: 'High',
    description: 'SuperTrend AI uses k-means clustering to dynamically adjust its ATR multiplier based on current market regime. Unlike static SuperTrend, it adapts to whether the market is trending or choppy.',
    signals: [
      { label: 'Trend Up', desc: 'Price above green SuperTrend line — stay long, trail stop to the line', color: '#22c55e' },
      { label: 'Trend Down', desc: 'Price below red SuperTrend line — stay short or avoid longs', color: '#ef4444' },
      { label: 'Flip Signal', desc: 'SuperTrend flips color — high probability trend change, enter on retest', color: '#f59e0b' },
    ],
    bestFor: ['Trend following', 'Stop placement', 'Swing trading'],
    timeframes: ['15m', '1H', 'Daily'],
    rules: [
      'Use SuperTrend as a dynamic trailing stop, not just entry signal',
      'AI version adjusts sensitivity — no manual tuning needed',
      'Works best on trending days, avoid in choppy consolidation',
      'Combine with VWAP for intraday confirmation',
    ],
    stats: 'SuperTrend AI outperforms static SuperTrend by 12-18% on backtests across trending markets',
  },
  {
    id: 'macd_momentum',
    icon: '📈',
    name: 'MACD Momentum Confirmation',
    category: 'Momentum',
    difficulty: 'Beginner',
    edge: 'Medium',
    description: 'MACD (Moving Average Convergence Divergence) measures momentum by comparing two EMAs. The histogram is the key — watch for it to turn green on a red day for powerful reversal signals.',
    signals: [
      { label: 'Bullish Cross', desc: 'MACD line crosses above signal line with expanding histogram — enter long', color: '#22c55e' },
      { label: 'Histogram Flip', desc: 'Histogram turns green after red bars — momentum shifting to buyers', color: '#60a5fa' },
      { label: 'Divergence', desc: 'Price makes new low but MACD makes higher low — bullish divergence, reversal coming', color: '#f59e0b' },
    ],
    bestFor: ['Trend confirmation', 'Reversals', 'Swing trading'],
    timeframes: ['5m', '15m', 'Daily'],
    rules: [
      'Never use MACD alone — always confirm with price action',
      'MACD cross above zero line = more powerful than signal line cross',
      'Divergence signals are the highest probability MACD setups',
      'Settings: 12, 26, 9 are standard — don\'t change them',
    ],
    stats: 'MACD bullish divergence on daily charts produces positive returns in 68% of cases within 20 trading days',
  },
  {
    id: 'rsi_extremes',
    icon: '⚡',
    name: 'RSI Extreme Levels',
    category: 'Momentum',
    difficulty: 'Beginner',
    edge: 'Medium',
    description: 'RSI measures speed of price movement. Readings above 70 signal overbought, below 30 signal oversold — but the real edge is in RSI divergence and extreme readings in strong trends.',
    signals: [
      { label: 'Oversold Bounce', desc: 'RSI <30 on a stock with strong fundamentals — look for reversal candle to buy', color: '#22c55e' },
      { label: 'Overbought Fade', desc: 'RSI >80 with bearish candle pattern — short the exhaustion', color: '#ef4444' },
      { label: 'RSI Divergence', desc: 'Price makes higher high but RSI makes lower high — hidden bearish divergence', color: '#f59e0b' },
    ],
    bestFor: ['Reversals', 'Mean reversion', 'Confirmation'],
    timeframes: ['5m', '15m', 'Daily'],
    rules: [
      'RSI >70 in a strong uptrend is normal — don\'t short just because it\'s overbought',
      'RSI(2) under 10 = extreme oversold — powerful mean reversion setup',
      'Divergence works better on higher timeframes (15m, 1H, Daily)',
      'Combine with support/resistance for high-probability entries',
    ],
    stats: 'RSI(2) <5 mean reversion setups produce 71% win rate on S&P 500 stocks over 20 years of backtesting',
  },
  {
    id: 'halt_resume',
    icon: '🛑',
    name: 'Halt & Resume Strategy',
    category: 'Special Situation',
    difficulty: 'Advanced',
    edge: 'Very High',
    description: 'Trading halts (T1, T2, LUDP) create forced pauses that reset momentum. The resume from a halt — especially on a stock already running — is one of the most explosive and reliable setups in small cap trading.',
    signals: [
      { label: 'Halt Up Resume', desc: 'Stock halted going up — buy the ask immediately on resume, target new HOD', color: '#22c55e' },
      { label: 'Halt Down Resume', desc: 'Stock halted going down — avoid longs, potential for continued flush', color: '#ef4444' },
      { label: 'Multiple Halts', desc: '2+ halts on same stock in one day = extreme momentum, treat each resume as new entry', color: '#f59e0b' },
    ],
    bestFor: ['Small cap momentum', 'Level 2 reading', 'Hot stocks'],
    timeframes: ['1m'],
    rules: [
      'Know the halt codes: T1 = regulatory, T2 = news pending, LUDP = circuit breaker',
      'T2 halts (news pending) often resume with gap — most explosive',
      'Set alerts on halt scanner so you don\'t miss the resume',
      'Size down on halt resumes — spreads are wide and moves are fast',
    ],
    stats: 'Stocks that experience 2+ halts in a day average 45%+ intraday moves from their open',
  },
];

const CATEGORIES = ['All', 'Intraday', 'Momentum', 'Volatility', 'Small Cap', 'Catalyst', 'Special Situation', 'Extended Hours', 'AI Indicator'];
const DIFFICULTIES = ['All', 'Beginner', 'Intermediate', 'Advanced'];

const EDGE_COLOR = { 'Very High': '#22c55e', 'High': '#60a5fa', 'Medium': '#f59e0b' };
const DIFF_COLOR = { 'Beginner': '#22c55e', 'Intermediate': '#f59e0b', 'Advanced': '#ef4444' };

export default function AIIndicators() {
  const [cat, setCat] = useState('All');
  const [diff, setDiff] = useState('All');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  const filtered = INDICATORS.filter(ind => {
    if (cat !== 'All' && ind.category !== cat) return false;
    if (diff !== 'All' && ind.difficulty !== diff) return false;
    if (search && !ind.name.toLowerCase().includes(search.toLowerCase()) && !ind.description.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="ai-page">
      {/* HEADER */}
      <div className="ai-header">
        <div className="ai-header-left">
          <div className="ai-header-icon">🤖</div>
          <div>
            <div className="ai-header-title">AI Indicators & Trading Edge</div>
            <div className="ai-header-sub">Proven setups used by professional traders — learn the edge behind each indicator</div>
          </div>
        </div>
        <div className="ai-header-stats">
          <div className="ai-stat"><span>{INDICATORS.length}</span><span>Indicators</span></div>
          <div className="ai-stat"><span>12</span><span>Strategies</span></div>
          <div className="ai-stat"><span>4</span><span>Categories</span></div>
        </div>
      </div>

      {/* FILTERS */}
      <div className="ai-filters">
        <input
          className="ai-search"
          placeholder="🔍  Search indicators..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="ai-filter-group">
          {CATEGORIES.map(c => (
            <button key={c} className={`ai-filter-btn ${cat === c ? 'active' : ''}`} onClick={() => setCat(c)}>{c}</button>
          ))}
        </div>
        <div className="ai-filter-group">
          {DIFFICULTIES.map(d => (
            <button key={d} className={`ai-diff-btn ${diff === d ? 'active' : ''}`} style={diff === d && d !== 'All' ? { borderColor: DIFF_COLOR[d], color: DIFF_COLOR[d] } : {}} onClick={() => setDiff(d)}>{d}</button>
          ))}
        </div>
      </div>

      {/* GRID */}
      <div className="ai-grid">
        {filtered.map(ind => (
          <div key={ind.id} className={`ai-card ${expanded === ind.id ? 'expanded' : ''}`}>
            {/* CARD HEADER */}
            <div className="ai-card-header" onClick={() => setExpanded(expanded === ind.id ? null : ind.id)}>
              <div className="ai-card-icon">{ind.icon}</div>
              <div className="ai-card-title-wrap">
                <div className="ai-card-name">{ind.name}</div>
                <div className="ai-card-meta">
                  <span className="ai-badge" style={{ color: DIFF_COLOR[ind.difficulty], borderColor: DIFF_COLOR[ind.difficulty] + '44' }}>{ind.difficulty}</span>
                  <span className="ai-badge cat">{ind.category}</span>
                  <span className="ai-badge" style={{ color: EDGE_COLOR[ind.edge], borderColor: EDGE_COLOR[ind.edge] + '44' }}>Edge: {ind.edge}</span>
                </div>
              </div>
              <div className="ai-card-timeframes">
                {ind.timeframes.map(tf => <span key={tf} className="ai-tf-badge">{tf}</span>)}
              </div>
              <div className="ai-card-chevron">{expanded === ind.id ? '▲' : '▼'}</div>
            </div>

            {/* DESCRIPTION */}
            <div className="ai-card-desc">{ind.description}</div>

            {/* SIGNALS */}
            <div className="ai-signals">
              {ind.signals.map((s, i) => (
                <div key={i} className="ai-signal" style={{ borderLeft: `3px solid ${s.color}` }}>
                  <span className="ai-signal-label" style={{ color: s.color }}>{s.label}</span>
                  <span className="ai-signal-desc">{s.desc}</span>
                </div>
              ))}
            </div>

            {/* EXPANDED CONTENT */}
            {expanded === ind.id && (
              <div className="ai-expanded">
                <div className="ai-expanded-grid">
                  {/* RULES */}
                  <div className="ai-rules">
                    <div className="ai-section-title">📋 Trading Rules</div>
                    {ind.rules.map((r, i) => (
                      <div key={i} className="ai-rule">
                        <span className="ai-rule-num">{i + 1}</span>
                        <span>{r}</span>
                      </div>
                    ))}
                  </div>

                  {/* BEST FOR + STATS */}
                  <div>
                    <div className="ai-section-title">✅ Best For</div>
                    <div className="ai-tags">
                      {ind.bestFor.map(b => <span key={b} className="ai-tag">{b}</span>)}
                    </div>
                    <div className="ai-stats-box">
                      <div className="ai-section-title" style={{ marginTop: 12 }}>📊 Research Data</div>
                      <div className="ai-stats-text">{ind.stats}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* DISCLAIMER */}
      <div className="ai-disclaimer">
        <span>⚠️</span>
        <span>Educational purposes only. All statistics are based on historical backtests and academic research. Past performance does not guarantee future results. Trading involves substantial risk of loss. Paper trade all strategies before using real capital.</span>
      </div>
    </div>
  );
}